import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip, 
  Tabs, 
  Tab,
  TextField,
  MenuItem,
  Grid,
  Button,
  Alert
} from '@mui/material';
import { api } from '../../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transactions-tabpanel-${index}`}
      aria-labelledby={`transactions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Transaction {
  operationId: number;
  extUid: string | null;
  type: string;
  userId: number;
  walletId: number;
  assetCode: string;
  assetName: string;
  amount: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface TransactionFilters {
  status: string;
  userId: string;
  assetId: string;
}

interface TransactionResponse {
  data: Transaction[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// API function to fetch transactions
const fetchTransactions = async (filters: TransactionFilters): Promise<TransactionResponse> => {
  try {
    const params: any = {};
    
    if (filters.status) params.status = filters.status;
    if (filters.userId) params.userId = parseInt(filters.userId);
    if (filters.assetId) params.assetId = parseInt(filters.assetId);
    
    params.limit = 9999;
    params.page = 1;

    console.log('Fetching transactions with params:', params);

    const response = await api.get('/api/v1/admin-panel/transactions', {
      params,
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });

    // Just return the data.data from the response
    return response.data.data;
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

const TransactionsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  
  // Filter states
  const [filters, setFilters] = useState<TransactionFilters>({
    status: '',
    userId: '',
    assetId: ''
  });

  // Load transactions
  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchTransactions(filters);
      
      if (response?.data && Array.isArray(response.data)) {
        setTransactions(response.data);
      } else {
        console.error('Invalid response structure:', response);
        setTransactions([]);
        setError('Не удалось загрузить транзакции: неверный формат данных');
      }
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      setTransactions([]);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке транзакций');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    loadTransactions();
  }, [filters]);

  // Filter transactions by type
  const deposits = transactions.filter(t => t.type === 'deposit');
  const withdrawals = transactions.filter(t => t.type === 'withdrawal');
  const allTransactions = [...transactions].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRefresh = () => {
    loadTransactions();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      userId: '',
      assetId: ''
    });
  };

  // Function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatExtUid = (extUid: string | null | undefined): string => {
    if (!extUid) return 'Н/Д';
    return `${extUid.substring(0, 6)}...${extUid.substring(extUid.length - 4)}`;
  };

  // Render filters
  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FilterListIcon sx={{ mr: 1 }} />
        Фильтры
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Статус"
            select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            size="small"
          >
            <MenuItem value="">Все статусы</MenuItem>
            <MenuItem value="pending">В ожидании</MenuItem>
            <MenuItem value="completed">Завершена</MenuItem>
            <MenuItem value="failed">Неуспешна</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="ID пользователя"
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            size="small"
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="ID актива"
            value={filters.assetId}
            onChange={(e) => handleFilterChange('assetId', e.target.value)}
            size="small"
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={clearFilters} size="small">
              Очистить
            </Button>
            <Button variant="contained" onClick={handleRefresh} size="small" startIcon={<RefreshIcon />}>
              Обновить
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  // Render operations table with proper data structure
  const renderOperationsTable = (data: any[], loading: boolean, error: any) => {
    if (loading) {
      return (
        <Box>
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Ошибка при загрузке данных: {error.message}
        </Alert>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Alert severity="info">
          Операции не найдены
        </Alert>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID операции</TableCell>
            <TableCell>Внешний ID</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Пользователь</TableCell>
            <TableCell>Кошелек</TableCell>
            <TableCell>Актив</TableCell>
            <TableCell>Сумма</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Дата</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((operation: any) => (
            <TableRow key={operation.operationId}>
              <TableCell>{operation.operationId}</TableCell>
              <TableCell>{formatExtUid(operation.extUid)}</TableCell>
              <TableCell>
                <Chip 
                  label={operation.type === 'deposit' ? 'Ввод' : 'Вывод'} 
                  color={operation.type === 'deposit' ? 'primary' : 'secondary'} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{operation.userId || 'Н/Д'}</TableCell>
              <TableCell>
                {operation.walletId ? `#${operation.walletId}` : 'Н/Д'}
              </TableCell>
              <TableCell>
                {operation.assetName} ({operation.assetCode})
              </TableCell>
              <TableCell>{operation.amount}</TableCell>
              <TableCell>
                <Chip 
                  label={operation.status} 
                  color={getStatusColor(operation.status)} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{new Date(operation.createdAt).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Транзакции
      </Typography>
      
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="transaction tabs">
            <Tab label="Все" />
            <Tab label="Вводы" />
            <Tab label="Выводы" />
          </Tabs>
        </Box>

        {/* All Transactions Tab */}
        <TabPanel value={tabValue} index={0}>
          {renderFilters()}
          <Paper sx={{ p: 2 }}>
            {renderOperationsTable(allTransactions, loading, error)}
          </Paper>
        </TabPanel>

        {/* Deposits Tab */}
        <TabPanel value={tabValue} index={1}>
          {renderFilters()}
          <Paper sx={{ p: 2 }}>
            {renderOperationsTable(deposits, loading, error)}
          </Paper>
        </TabPanel>

        {/* Withdrawals Tab */}
        <TabPanel value={tabValue} index={2}>
          {renderFilters()}
          <Paper sx={{ p: 2 }}>
            {renderOperationsTable(withdrawals, loading, error)}
          </Paper>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default TransactionsPage; 